/**
 * Track geometry helpers shared by the elevation chart, the distance markers and the
 * route quality overlay.
 */

import { LatLng } from '../models/brouter-map.model';

const EARTH_RADIUS_M = 6_371_000;

const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance in metres. Accurate enough at track scale. */
export const distanceMetres = (a: LatLng, b: LatLng): number => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
};

/** Running distance from the start of the track, in metres, one entry per point. */
export const cumulativeDistances = (track: readonly LatLng[]): number[] => {
  const distances: number[] = [];
  let total = 0;

  track.forEach((point, i) => {
    if (i > 0) {
      total += distanceMetres(track[i - 1], point);
    }
    distances.push(total);
  });

  return distances;
};

/** Running distance in kilometres, rounded for chart axis use. */
export const cumulativeDistancesKm = (track: readonly LatLng[]): number[] =>
  cumulativeDistances(track).map(metres => Math.round((metres / 1000) * 1000) / 1000);

export interface DistanceMarker {
  /** Whole kilometres from the start. */
  km: number;
  position: LatLng;
}

/**
 * Chooses a marker spacing that keeps the map readable.
 *
 * The legacy plugin varied spacing with zoom; this varies it with track length, which
 * behaves the same way in practice without re-rendering on every zoom.
 */
export const pickMarkerSpacingKm = (totalMetres: number): number => {
  const totalKm = totalMetres / 1000;
  if (totalKm <= 20) {
    return 1;
  }
  if (totalKm <= 60) {
    return 5;
  }
  if (totalKm <= 200) {
    return 10;
  }
  if (totalKm <= 600) {
    return 25;
  }
  return 50;
};

/** Positions of the whole-kilometre markers along the track. */
export const buildDistanceMarkers = (track: readonly LatLng[]): DistanceMarker[] => {
  if (track.length < 2) {
    return [];
  }

  const distances = cumulativeDistances(track);
  const total = distances[distances.length - 1];
  const spacingKm = pickMarkerSpacingKm(total);
  const markers: DistanceMarker[] = [];

  let nextKm = spacingKm;
  for (let i = 1; i < track.length; i++) {
    // A single long leg can span several marker intervals.
    while (distances[i] >= nextKm * 1000 && nextKm * 1000 <= total) {
      markers.push({ km: nextKm, position: track[i] });
      nextKm += spacingKm;
    }
  }

  return markers;
};
